import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Mail, Lock, Smartphone, AlertCircle, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import SEO from '../../components/SEO';

const CausesLogin = () => {
  const navigate = useNavigate();
  const [authMethod, setAuthMethod] = useState('mobile'); // 'mobile', 'email', 'google'
  const [step, setStep] = useState(1); // 1: enter phone/email, 2: verify OTP/password
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    phoneOrEmail: '',
    otp: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [otpSent, setOtpSent] = useState(false);

  const API = process.env.REACT_APP_BACKEND_URL;

  const handleSendOTP = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/api/petitions/auth/send-otp`,
        null,
        { params: { phone_or_email: formData.phoneOrEmail }}
      );
      
      toast.success('OTP sent successfully! Check your phone/email.');
      setOtpSent(true);
      setStep(2);
      
      // Show OTP in toast for testing (remove in production)
      if (response.data.otp) {
        toast.info(`Testing OTP: ${response.data.otp}`, { duration: 10000 });
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${API}/api/petitions/auth/verify-otp`, {
        phone_or_email: formData.phoneOrEmail,
        otp: formData.otp
      });

      // Store token and user data
      localStorage.setItem('causes_token', response.data.access_token);
      localStorage.setItem('causes_user', JSON.stringify(response.data.campaigner));

      toast.success('Login successful!');
      
      // Check if profile is complete
      if (!response.data.campaigner.name || !response.data.campaigner.city) {
        navigate('/causes/profile/complete');
      } else {
        navigate('/causes/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${API}/api/petitions/auth/login`, {
        email_or_phone: formData.phoneOrEmail,
        password: formData.password,
        auth_method: 'email_password'
      });

      // Store token and user data
      localStorage.setItem('causes_token', response.data.access_token);
      localStorage.setItem('causes_user', JSON.stringify(response.data.campaigner));

      toast.success('Login successful!');
      navigate('/causes/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid email or password.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    toast.info('Google login will be integrated soon!');
    // TODO: Implement Google OAuth
  };

  return (
    <div className="causes-login-page">
      <SEO title="Login - Elderra Causes" description="Login to your Elderra Causes account" />

      <div className="causes-login-container">
        <div className="causes-login-card">
          <Link to="/causes" className="back-link">
            <ArrowLeft size={20} />
            Back to Home
          </Link>

          <div className="causes-login-header">
            <img 
              src="https://customer-assets.emergentagent.com/job_digital-seniors-3/artifacts/fqevhgzz_Elderra%20logo%20new%20original%20%281%29.png" 
              alt="Elderra Logo"
              className="causes-login-logo"
            />
            <h1>Welcome Back</h1>
            <p>Login to manage your petitions</p>
          </div>

          {error && (
            <div className="causes-error-alert">
              <AlertCircle size={20} />
              <span>{error}</span>
            </div>
          )}

          {/* Auth Method Selection */}
          <div className="auth-method-tabs">
            <button
              className={`auth-tab ${authMethod === 'mobile' ? 'active' : ''}`}
              onClick={() => {
                setAuthMethod('mobile');
                setStep(1);
                setOtpSent(false);
                setError('');
              }}
            >
              <Smartphone size={20} />
              Mobile OTP
            </button>
            <button
              className={`auth-tab ${authMethod === 'email' ? 'active' : ''}`}
              onClick={() => {
                setAuthMethod('email');
                setStep(1);
                setError('');
              }}
            >
              <Mail size={20} />
              Email
            </button>
            <button
              className={`auth-tab ${authMethod === 'google' ? 'active' : ''}`}
              onClick={() => setAuthMethod('google')}
            >
              <svg width="20" height="20" viewBox="0 0 24 24">
                <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Google
            </button>
          </div>

          {/* Mobile OTP Flow */}
          {authMethod === 'mobile' && (
            <form onSubmit={step === 1 ? handleSendOTP : handleVerifyOTP} className="causes-login-form">
              {step === 1 ? (
                <>
                  <div className="form-group">
                    <label>Mobile Number or Email</label>
                    <Input
                      type="text"
                      value={formData.phoneOrEmail}
                      onChange={(e) => setFormData({ ...formData, phoneOrEmail: e.target.value })}
                      placeholder="Enter your mobile number or email"
                      required
                      disabled={loading}
                    />
                    <p className="form-hint">We'll send you a verification code</p>
                  </div>
                  <Button type="submit" className="submit-button" disabled={loading}>
                    {loading ? 'Sending OTP...' : 'Send OTP'}
                  </Button>
                </>
              ) : (
                <>
                  <div className="form-group">
                    <label>Enter OTP</label>
                    <Input
                      type="text"
                      value={formData.otp}
                      onChange={(e) => setFormData({ ...formData, otp: e.target.value })}
                      placeholder="Enter 6-digit OTP"
                      maxLength={6}
                      required
                      disabled={loading}
                    />
                    <p className="form-hint">OTP sent to {formData.phoneOrEmail}</p>
                  </div>
                  <Button type="submit" className="submit-button" disabled={loading}>
                    {loading ? 'Verifying...' : 'Verify & Login'}
                  </Button>
                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    className="text-link"
                    disabled={loading}
                  >
                    Change number
                  </button>
                </>
              )}
            </form>
          )}

          {/* Email/Password Flow */}
          {authMethod === 'email' && (
            <form onSubmit={handleEmailLogin} className="causes-login-form">
              <div className="form-group">
                <label>Email Address</label>
                <Input
                  type="email"
                  value={formData.phoneOrEmail}
                  onChange={(e) => setFormData({ ...formData, phoneOrEmail: e.target.value })}
                  placeholder="your.email@example.com"
                  required
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label>Password</label>
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Enter your password"
                  required
                  disabled={loading}
                />
              </div>

              <Button type="submit" className="submit-button" disabled={loading}>
                {loading ? 'Logging in...' : 'Login'}
              </Button>
            </form>
          )}

          {/* Google Login */}
          {authMethod === 'google' && (
            <div className="google-login-section">
              <button onClick={handleGoogleLogin} className="google-login-button">
                <svg width="24" height="24" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC04" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </button>
              <p className="google-note">Quick and secure login with your Google account</p>
            </div>
          )}

          <div className="causes-login-footer">
            <p>Don't have an account?</p>
            <Link to="/causes/register" className="register-link">
              Register Now
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CausesLogin;
